import React, { useEffect, useMemo, useRef, useState } from "react";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Callout, Checkbox, DefaultButton, DirectionalHint, Link, SearchBox, Spinner, SpinnerSize, Stack } from "office-ui-fabric-react";
import {
    filter, setFinished, toggleFiltration,
} from "../../../../config/redux/filters/filterActions";
import { searchBoxStyle } from "../../styles/searchBox.style";
import { activeSearchModeTabS, inactiveSearchModeTabS, searchModeActionsS, searchModeButtonS, searchModeCalloutContentS, searchModeCheckboxS, searchModeControlsWidth, searchModeFieldColumnS, searchModeFieldsColumnsS, searchModeFieldsS, searchModeSearchBoxWidth, searchModeSectionTitle } from "../../styles/searchMode.styles";
import { ISearchModeConfig } from "./searchModes.config";

interface ISearchBoxProps {
    reload: (data: any) => any;
    placeholder: string;
    searchModeConfig?: ISearchModeConfig;
}

type SearchMode = "simple" | "advanced";

const getSelectableFields = (searchModeConfig?: ISearchModeConfig) => {
    if (!searchModeConfig) {
        return [];
    }

    return [...searchModeConfig.simpleFields, ...searchModeConfig.advancedFields];
};

const createAdvancedSelection = (searchModeConfig?: ISearchModeConfig) => {
    const simpleFieldKeys = searchModeConfig ? searchModeConfig.simpleFields.map((field) => field.key) : [];

    return getSelectableFields(searchModeConfig).reduce<Record<string, boolean>>((result, field) => {
        result[field.key] = simpleFieldKeys.includes(field.key);

        return result;
    }, {});
};

const SearchBoxCommon: React.FC<ISearchBoxProps> = ({ reload, placeholder, searchModeConfig }) => {
    const { t } = useTranslation("admin");
    const dispatch = useDispatch();

    const { finished } = useSelector((state: RootStateOrAny) => state.filtration);

    const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
    const [isSearchSubmitting, setIsSearchSubmitting] = useState(false);
    const [searchMode, setSearchMode] = useState<SearchMode>("simple");
    const [isCalloutVisible, setIsCalloutVisible] = useState(false);
    const [advancedSelection, setAdvancedSelection] = useState<Record<string, boolean>>(createAdvancedSelection(searchModeConfig));
    const modeButtonRef = useRef<HTMLDivElement | null>(null);
    const selectableFields = getSelectableFields(searchModeConfig);
    const isSimpleMode = searchMode === "simple";

    const getSelectedFields = () => {
        if (!searchModeConfig) {
            return undefined;
        }

        if (isSimpleMode) {
            return searchModeConfig.simpleFields.map((field) => field.key);
        }

        return selectableFields
            .filter((field) => advancedSelection[field.key])
            .map((field) => field.key);
    };

    useEffect(() => {
        setSearchMode("simple");
        setIsCalloutVisible(false);
        setAdvancedSelection(createAdvancedSelection(searchModeConfig));
    }, [searchModeConfig]);

    useEffect(() => {
        if (finished) {
            setIsSearchSubmitting(false);
        }
    }, [finished]);

    const visibleFields = useMemo(() => {
        if (!searchModeConfig) {
            return [];
        }

        return isSimpleMode
            ? searchModeConfig.simpleFields
            : selectableFields;
    }, [isSimpleMode, searchModeConfig, selectableFields]);

    const visibleFieldColumns = useMemo(() => {
        if (isSimpleMode) {
            return [visibleFields];
        }

        const firstColumnLength = Math.ceil(visibleFields.length / 2);

        return [
            visibleFields.slice(0, firstColumnLength),
            visibleFields.slice(firstColumnLength),
        ];
    }, [isSimpleMode, visibleFields]);

    function setfiltration(value?: string) {
        const phrase = value !== undefined ? value : searchTerm;

        if (phrase !== undefined && finished === true) {
            setIsSearchSubmitting(true);
            dispatch(setFinished({ finished: false }));
            dispatch(filter({
                searchPhrase: phrase,
                searchFields: getSelectedFields(),
                searchMode: searchModeConfig ? searchMode : undefined,
            }));
            dispatch(toggleFiltration({ filter: true }));
            dispatch(reload({ reload: true }));
        }
    }

    const isSearchDisabled = isSearchSubmitting && !finished;

    const handleSubmit = (event: { preventDefault: () => void; }) => {
        event.preventDefault();
        setfiltration();
    };

    const handleAdvancedFieldChange = (fieldKey: string, checked?: boolean) => {
        setAdvancedSelection((currentValue) => ({
            ...currentValue,
            [fieldKey]: !!checked,
        }));
    };

    const clearAdvancedSelection = () => {
        setAdvancedSelection(selectableFields.reduce<Record<string, boolean>>((result, field) => {
            result[field.key] = false;

            return result;
        }, {}));
    };

    const selectAllAdvancedFields = () => {
        setAdvancedSelection(selectableFields.reduce<Record<string, boolean>>((result, field) => {
            result[field.key] = true;

            return result;
        }, {}));
    };

    const searchBoxStyles = {
        ...searchBoxStyle,
        root: Object.assign({}, searchBoxStyle.root, {
            width: searchModeConfig ? searchModeSearchBoxWidth : "320px",
            marginLeft: searchModeConfig ? -1 : 0,
            borderTopLeftRadius: searchModeConfig ? "0" : undefined,
            borderBottomLeftRadius: searchModeConfig ? "0" : undefined,
        }),
    };


    return (
        <form onSubmit={handleSubmit} >
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 0 }}>
                {searchModeConfig && (
                    <div ref={modeButtonRef}>
                        <DefaultButton
                            text={searchMode === "simple" ? (t("nav.simple") as string) : (t("nav.advanced") as string)}
                            menuIconProps={{ iconName: "ChevronDown" }}
                            onClick={() => setIsCalloutVisible((currentValue) => !currentValue)}
                            disabled={isSearchDisabled}
                            styles={searchModeButtonS}
                        />
                    </div>
                )}

                <SearchBox
                    styles={searchBoxStyles}
                    placeholder={placeholder}
                    name="searchPhrase"
                    onChange={(newValue) => { setSearchTerm(newValue); }}
                    onSearch={(newValue) => {
                        setSearchTerm(newValue);
                        setfiltration(newValue);
                    }}
                    type="text"
                    disableAnimation={true}
                    disabled={isSearchDisabled}
                />
                {isSearchDisabled && <Spinner size={SpinnerSize.medium} style={{ marginLeft: 8 }} />}
            </Stack>
            {searchModeConfig && isCalloutVisible && modeButtonRef.current && (
                <Callout
                    target={modeButtonRef.current}
                    onDismiss={() => setIsCalloutVisible(false)}
                    directionalHint={DirectionalHint.bottomLeftEdge}
                    setInitialFocus
                    calloutWidth={searchModeControlsWidth}
                >
                    <Stack
                        tokens={{ childrenGap: 12 }}
                        styles={searchModeCalloutContentS}
                    >
                        <Stack horizontal tokens={{ childrenGap: 8 }}>
                            <DefaultButton
                                text={t("nav.simple") as string}
                                onClick={() => setSearchMode("simple")}
                                styles={isSimpleMode ? activeSearchModeTabS : inactiveSearchModeTabS}
                            />
                            <DefaultButton
                                text={t("nav.advanced") as string}
                                onClick={() => setSearchMode("advanced")}
                                styles={!isSimpleMode ? activeSearchModeTabS : inactiveSearchModeTabS}
                            />
                        </Stack>

                        <span className={searchModeSectionTitle}>
                            {isSimpleMode ? t("nav.simpleSearch") : t("nav.advancedSearch")}
                        </span>

                        <Stack
                            tokens={{ childrenGap: 6 }}
                            styles={searchModeFieldsS}
                        >
                            <Stack horizontal={!isSimpleMode} tokens={{ childrenGap: 16 }} styles={searchModeFieldsColumnsS}>
                                {visibleFieldColumns.map((columnFields, index) => (
                                    <Stack key={index} tokens={{ childrenGap: 6 }} styles={!isSimpleMode ? searchModeFieldColumnS : undefined}>
                                        {columnFields.map((field) => (
                                            <Checkbox
                                                key={field.key}
                                                label={field.label}
                                                styles={searchModeCheckboxS}
                                                checked={isSimpleMode ? true : advancedSelection[field.key]}
                                                disabled={isSimpleMode}
                                                onChange={(_, checked) => handleAdvancedFieldChange(field.key, checked)}
                                            />
                                        ))}
                                    </Stack>
                                ))}
                            </Stack>
                        </Stack>

                        {!isSimpleMode && (
                            <Stack horizontal tokens={{ childrenGap: 12 }}>
                                <Link styles={searchModeActionsS} onClick={selectAllAdvancedFields}>
                                    {t("nav.selectAll")}
                                </Link>
                                <Link styles={searchModeActionsS} onClick={clearAdvancedSelection}>
                                    {t("nav.clearSelection")}
                                </Link>
                            </Stack>
                        )}
                    </Stack>
                </Callout>
            )}
        </form>
    );
};

export default SearchBoxCommon;